import { useEffect, useState } from 'react';
import { ApiError, type ApiClient } from '../api/client';

export type PersonalAgentAccessRequest = {
  id: string; event_id: string; conversation_id: string; conversation_title: string;
  purpose: string; status: string; duration: 'task' | 'ongoing' | null;
  expires_at: string | null; request_expires_at: string; revoked_at: string | null;
  source_grant_id: string | null;
};
type Decision = { id: string; decision: 'confirm' | 'deny' | 'revoke'; duration?: 'task' | 'ongoing' };

function AccessCard({ access, apiClient, refresh, now, apiPrefix, allowOngoing }: {
  access: PersonalAgentAccessRequest; apiClient: ApiClient; refresh: () => Promise<void>; now: number; apiPrefix: string; allowOngoing: boolean;
}) {
  const [duration, setDuration] = useState<'task' | 'ongoing'>('task');
  const [busy, setBusy] = useState(false);
  const [uncertain, setUncertain] = useState<Decision | null>(null);
  const [error, setError] = useState<string | null>(null);
  const decide = async (decision: Decision) => {
    if (busy) return;
    setBusy(true); setError(null);
    let accepted = false;
    try {
      await apiClient.post(`${apiPrefix}/access/decide`, decision);
      accepted = true;
      setUncertain(null);
      await refresh();
    } catch (failure) {
      if (!accepted && failure instanceof ApiError && failure.status >= 400 && failure.status < 500) {
        setUncertain(null);
        setError('The server refused this answer. Refresh to check the saved state; a pending request can still be denied to continue without access.');
      } else {
        setUncertain(decision);
        setError('Decision not confirmed. Retry the same answer or refresh to check the saved state. A retry cannot extend access or replay completed reads.');
      }
    } finally { setBusy(false); }
  };
  const pending = access.status === 'pending' || access.status === 'expired';
  const expired = Date.parse(access.request_expires_at) <= now;
  return <article aria-label={`Conversation access: ${access.conversation_title}`}>
    <h3>{access.conversation_title}</h3>
    <p>Conversation: <code>{access.conversation_id}</code></p>
    <p>Operation: Read saved messages (at most 10 messages, 1,000 characters each; no writes).</p>
    <p>Saved task: {access.purpose}</p>
    {pending ? <>
      <p>For this task I need access to this private conversation. Deny continues this task without reading it.</p>
      {allowOngoing ? <label>Access duration <select value={duration} disabled={busy || !!uncertain}
        onChange={event => setDuration(event.target.value as 'task' | 'ongoing')}>
        <option value="task">This task only (up to 15 minutes)</option>
        <option value="ongoing">Ongoing for this conversation, until revoked</option>
      </select></label> : <p>This task only (up to 15 minutes).</p>}
      {expired && <p>This request expired. Deny to continue without access.</p>}
      <button type="button" disabled={busy || !!uncertain || expired}
        onClick={() => { void decide({ id: access.id, decision: 'confirm', duration }); }}>Confirm access</button>
      <button type="button" disabled={busy || !!uncertain}
        onClick={() => { void decide({ id: access.id, decision: 'deny' }); }}>Deny access</button>
    </> : <>
      <p>{access.duration === 'ongoing' ? 'Ongoing access for future tasks' : 'Access for this task only'}. Revoke prevents future reads; content already read cannot be withdrawn.</p>
      <button type="button" disabled={busy || !!uncertain}
        onClick={() => { void decide({ id: access.id, decision: 'revoke' }); }}>Revoke conversation access</button>
    </>}
    {error && <p role="alert">{error}</p>}
    {uncertain && <button type="button" disabled={busy}
      onClick={() => { void decide(uncertain); }}>Retry same answer</button>}
  </article>;
}

export default function PersonalAgentAccess({ requests, apiClient, refresh, apiPrefix = '/api/personal-agent', allowOngoing = true }: {
  requests: PersonalAgentAccessRequest[]; apiClient: ApiClient; refresh: () => Promise<void>; apiPrefix?: string; allowOngoing?: boolean;
}) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(timer);
  }, []);
  const visible = requests.filter(access => access.status === 'pending' || access.status === 'expired' || (
    access.status === 'approved' && !access.revoked_at && !access.source_grant_id &&
    (!access.expires_at || Date.parse(access.expires_at) > now)
  ));
  if (!visible.length) return null;
  return <section className="card" aria-label="Conversation permissions"><h2>Conversation permissions</h2>
    {visible.map(access => <AccessCard key={access.id} access={access} apiClient={apiClient} refresh={refresh} now={now} apiPrefix={apiPrefix} allowOngoing={allowOngoing} />)}
  </section>;
}
